import { useState } from 'react'
import { motion } from 'framer-motion'
import type { Task } from '../types/task'
import type { Client, Shoot } from '../types'
import { 
  ClockIcon,
  CalendarIcon,
  UserIcon,
  CameraIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  PlayIcon,
  PauseIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  FireIcon,
  ArrowUpIcon,
  MinusIcon
} from '@heroicons/react/24/outline'

interface TaskCardProps {
  task: Task
  index: number
  onUpdate: (taskId: string, updates: any) => void
  onDelete: (taskId: string) => void
  clients: Client[]
  shoots: Shoot[]
}

const TaskCard = ({ task, index, onUpdate, onDelete, clients, shoots }: TaskCardProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState(task.title)

  const getPriorityIcon = (priority: Task['priority']) => {
    switch (priority) {
      case 'urgent':
        return <FireIcon className="w-4 h-4 text-red-400" />
      case 'high':
        return <ArrowUpIcon className="w-4 h-4 text-orange-400" />
      case 'medium':
        return <MinusIcon className="w-4 h-4 text-yellow-400" />
      case 'low':
        return <MinusIcon className="w-4 h-4 text-gray-400" />
    }
  }

  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'urgent':
        return 'border-red-500/30 bg-red-500/10'
      case 'high':
        return 'border-orange-500/30 bg-orange-500/10'
      case 'medium':
        return 'border-yellow-500/30 bg-yellow-500/10'
      case 'low':
        return 'border-gray-500/30 bg-gray-500/10'
    }
  }

  const getCategoryIcon = (category: Task['category']) => {
    switch (category) {
      case 'shoot_prep':
        return <CameraIcon className="w-4 h-4" />
      case 'editing':
        return <PencilIcon className="w-4 h-4" />
      case 'client_communication':
        return <UserIcon className="w-4 h-4" />
      default:
        return <ClockIcon className="w-4 h-4" />
    }
  }

  const formatDueDate = (dueDate: string) => {
    const date = new Date(dueDate)
    const today = new Date()
    const diffTime = date.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays < 0) {
      return { text: `${Math.abs(diffDays)} days overdue`, color: 'text-red-400' }
    } else if (diffDays === 0) {
      return { text: 'Due today', color: 'text-orange-400' }
    } else if (diffDays === 1) {
      return { text: 'Due tomorrow', color: 'text-yellow-400' }
    } else if (diffDays <= 7) {
      return { text: `Due in ${diffDays} days`, color: 'text-blue-400' }
    } else {
      return { text: date.toLocaleDateString(), color: 'text-gray-400' }
    }
  }

  const handleStatusChange = (newStatus: Task['status']) => {
    onUpdate(task.id, { status: newStatus })
  }

  const handleSaveEdit = () => {
    if (editTitle.trim() && editTitle !== task.title) {
      onUpdate(task.id, { title: editTitle.trim() })
    }
    setIsEditing(false)
  }

  const handleCancelEdit = () => {
    setEditTitle(task.title)
    setIsEditing(false)
  }

  const client = clients.find(c => c.id === task.clientId)
  const shoot = shoots.find(s => s.id === task.shootId)
  const dueInfo = task.dueDate ? formatDueDate(task.dueDate) : null

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`p-4 rounded-xl border transition-all hover:scale-[1.02] ${getPriorityColor(task.priority)}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2">
          {getPriorityIcon(task.priority)}
          <span className="text-xs font-medium text-gray-400 uppercase">
            {task.priority}
          </span>
        </div>
        
        <div className="flex items-center space-x-1">
          {/* Status Change Buttons */}
          {task.status === 'pending' && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => handleStatusChange('in_progress')}
              className="p-1 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 transition-colors"
              title="Start task"
            >
              <PlayIcon className="w-4 h-4" />
            </motion.button>
          )}
          
          {task.status === 'in_progress' && (
            <>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => handleStatusChange('completed')}
                className="p-1 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 transition-colors"
                title="Complete task"
              >
                <CheckIcon className="w-4 h-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => handleStatusChange('pending')}
                className="p-1 rounded-lg bg-gray-500/20 hover:bg-gray-500/30 text-gray-400 transition-colors"
                title="Pause task"
              >
                <PauseIcon className="w-4 h-4" />
              </motion.button>
            </>
          )}
          
          {(task.status === 'pending' || task.status === 'in_progress') && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => handleStatusChange('cancelled')}
              className="p-1 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-colors"
              title="Cancel task"
            >
              <XMarkIcon className="w-4 h-4" />
            </motion.button>
          )}
          
          {task.status === 'completed' && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => handleStatusChange('pending')}
              className="p-1 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 transition-colors"
              title="Reopen task"
            >
              <PlayIcon className="w-4 h-4" />
            </motion.button>
          )}
          
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => onDelete(task.id)}
            className="p-1 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-colors"
            title="Delete task"
          >
            <TrashIcon className="w-4 h-4" />
          </motion.button>
        </div>
      </div>

      {/* Title */}
      <div className="mb-3">
        {isEditing ? (
          <div className="space-y-2">
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveEdit()
                if (e.key === 'Escape') handleCancelEdit()
              }}
            />
            <div className="flex space-x-2">
              <button
                onClick={handleSaveEdit}
                className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs hover:bg-green-500/30 transition-colors"
              >
                Save
              </button>
              <button
                onClick={handleCancelEdit}
                className="px-2 py-1 bg-gray-500/20 text-gray-400 rounded text-xs hover:bg-gray-500/30 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <h4 
            className="text-white font-medium text-sm cursor-pointer hover:text-blue-400 transition-colors"
            onClick={() => setIsEditing(true)}
          >
            {task.title}
          </h4>
        )}
      </div>

      {/* Description */}
      {task.description && (
        <p className="text-gray-400 text-xs mb-3 line-clamp-2">
          {task.description}
        </p>
      )}

      {/* Category & Time */}
      <div className="flex items-center space-x-3 mb-3">
        <div className="flex items-center space-x-1 text-gray-400">
          {getCategoryIcon(task.category)}
          <span className="text-xs capitalize">
            {task.category.replace('_', ' ')}
          </span>
        </div>
        
        {task.estimatedMinutes && (
          <div className="flex items-center space-x-1 text-gray-400">
            <ClockIcon className="w-3 h-3" />
            <span className="text-xs">
              {task.estimatedMinutes}m
            </span>
          </div>
        )}
      </div>

      {/* Due Date */}
      {dueInfo && (
        <div className={`flex items-center space-x-1 mb-3 ${dueInfo.color}`}>
          <CalendarIcon className="w-3 h-3" />
          <span className="text-xs font-medium">
            {dueInfo.text}
          </span>
          {dueInfo.color === 'text-red-400' && (
            <ExclamationTriangleIcon className="w-3 h-3" />
          )}
        </div>
      )}

      {/* Related Entities */}
      {(client || shoot) && (
        <div className="space-y-1">
          {client && (
            <div className="flex items-center space-x-1 text-gray-500">
              <UserIcon className="w-3 h-3" />
              <span className="text-xs truncate">
                {client.name}
              </span>
            </div>
          )}
          
          {shoot && (
            <div className="flex items-center space-x-1 text-gray-500">
              <CameraIcon className="w-3 h-3" />
              <span className="text-xs truncate">
                {shoot.title}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Tags */}
      {task.tags && task.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3">
          {task.tags.slice(0, 3).map((tag, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-white/10 text-gray-400 rounded text-xs"
            >
              {tag}
            </span>
          ))}
          {task.tags.length > 3 && (
            <span className="px-2 py-1 bg-white/10 text-gray-400 rounded text-xs">
              +{task.tags.length - 3}
            </span>
          )}
        </div>
      )}
    </motion.div>
  )
}

export default TaskCard 